import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { ButtonComponent } from '../../components';
import { ErrorCardComponent } from '../../components/card/error-card/error-card.component';
import { InputComponent } from '../../components/inputs/input/input.component';
import { ClientUserService, ErrorService, GuardFinderError, ProUserService } from '../../services';
import { AuthService } from '../../services/auth/auth.service';
import { LoadingService } from '../../services/loading/loading.service';
import { NotificationsService } from '../../services/notifications/notifications.service';
import { analyticsPageNames } from '../../shared/analytics-page-names';
import { GlobalsInterface, LoginApiResponse, UserApiDataInterface } from '../../types';

@Component({
    selector: 'lib-login-form',
    standalone: true,
    imports: [CommonModule, ButtonComponent, ReactiveFormsModule, InputComponent, ErrorCardComponent],
    templateUrl: './login-form.component.html',
    styleUrl: './login-form.component.css'
})
export class LoginFormComponent implements OnDestroy, OnInit {
    clientUserService = inject(ClientUserService);
    proUserService = inject(ProUserService);

    globals!: GlobalsInterface;

    @Input() initialEmail: string | null = null;

    @Output() login: EventEmitter<LoginApiResponse> = new EventEmitter();
    @Output() userGet: EventEmitter<UserApiDataInterface> = new EventEmitter();

    submitAttempt: boolean = false;

    submissionError: object | null = null;

    loginFormGroup: FormGroup = new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
        password: new FormControl('', Validators.required)
    });

    constructor(
        private authService: AuthService,
        private loadService: LoadingService,
        private nService: NotificationsService,
        private errorService: ErrorService,
        @Inject('globals') globals: GlobalsInterface
    ) {
        this.globals = globals;
    }

    ngOnInit(): void {
        this.loginFormGroup.controls['password'].reset();

        if (this.initialEmail) {
            this.loginFormGroup.patchValue({
                email: this.initialEmail
            });
        }
    }

    ngOnDestroy() {
        this.submitAttempt = false;
        this.submissionError = null;
        this.loginFormGroup.reset();
    }

    async onSubmit() {
        this.submissionError = null;
        this.submitAttempt = true;

        if (!this.loginFormGroup.valid) {
            this.submissionError = this.loginFormGroup.errors || null;
            this.login.emit({ success: false, data: { token: '' } });
            const errors = GuardFinderError.createFromFormGroupErrors(this.loginFormGroup);
            await this.errorService.showTypedErrorsModal(errors);
            return;
        }

        if (this.globals.appType === 'client') {
            this.clientUserService.setUser(null);
        } else {
            this.proUserService.setUser(null);
        }

        await this.loadService.presentLoading();
        this.authService.login(this.loginFormGroup.value).subscribe({
            next: data => {
                if (data.success) {
                    this.authService.saveToken(data.data.token);
                    this.submitAttempt = false;
                    this.submissionError = null;
                    this.loginFormGroup.reset();

                    this.getUserData(data.data.token);
                    this.login.emit(data);
                    return;
                }

                // Additional block to check if an API somewhy returned 200 success response with the error in it
                this.login.emit(data);
                this.errorService.apiErrors(analyticsPageNames.login, data);

                if (data.error) {
                    if (typeof data.error === 'string') {
                        this.submissionError = {
                            error: data.error
                        };
                    } else {
                        this.submissionError = data.error['statusText'] || data.error['message'] || data.error;
                    }
                }
            },
            error: error => {
                console.log('error');
                console.log(error);
                this.login.emit(error);
                this.submissionError = error;

                const errorMessage = error.message ? error.message : error.statusText;

                this.errorService.apiErrors(analyticsPageNames.login, errorMessage);
                this.loadService.dismissLoading();
            },
            complete: () => this.loadService.dismissLoading()
        });
    }

    getUserData(token: string) {
        if (this.globals.appType === 'client') {
            this.clientUserService.getUserWithToken(token).subscribe(user => {
                if (user.success) {
                    this.nService.startNotificationSubscription();
                }
                this.userGet.emit(user as UserApiDataInterface);
            });
            return;
        }
        this.proUserService.getUserWithToken(token).subscribe(user => {
            if (user.success) {
                this.nService.startNotificationSubscription();
            }
            this.userGet.emit(user as UserApiDataInterface);
        });
    }
}
